import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseCategories, caseStatuses, priceModels } from '../../types/admin';
import type { CaseCategory, CaseStatus, PriceModel } from '../../types/admin';
const NEW_CUSTOMER = 'ny';
export function CaseNew() {
  const {
    customers,
    createCase,
    settings
  } = useAdminData();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? NEW_CUSTOMER);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CaseCategory>('Multiservice');
  const [priceModel, setPriceModel] = useState<PriceModel>('Tilbud endnu ikke lavet');
  const [hourlyRate, setHourlyRate] = useState(settings.hourlyRate);
  const [fixedPrice, setFixedPrice] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [status, setStatus] = useState<CaseStatus>('Ny henvendelse');
  const [internalNote, setInternalNote] = useState('');
  const [photos, setPhotos] = useState<{
    url: string;
    name: string;
  }[]>([]);
  const creatingCustomer = customerId === NEW_CUSTOMER;
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-291" data-mp="w3m5ei9nas8u">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-292" data-mp="hdy3pfxjxrkl">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-293" data-mp="8botysa3y289" />
        Sager
      </Link>
      <h1 className="mt-4 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-294" data-mp="db1pe1f1ohmg">
        Opret sag
      </h1>

      <form className="mt-7 max-w-3xl space-y-5" onSubmit={event => {
      event.preventDefault();
      const created = createCase({
        customerId: creatingCustomer ? '' : customerId,
        newCustomer: creatingCustomer ? {
          name,
          phone,
          email,
          address
        } : undefined,
        title,
        address,
        description,
        category,
        priceModel,
        hourlyRate: priceModel === 'Timepris' ? hourlyRate : '',
        fixedPrice: priceModel === 'Fast pris' ? fixedPrice : '',
        plannedDate,
        status,
        internalNote,
        photos
      });
      navigate(`/admin/sager/${created.id}`);
    }} data-id="element-295" data-mp="2f21vddf13vd2">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-296" data-mp="1djhy2vutx9xt">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-297" data-mp="1dkt2mxw3mlpz">Kunde</h2>
          <SelectField id="ns-kunde" label="Vælg kunde" className="mt-5" value={customerId} onChange={event => setCustomerId(event.target.value)} options={[...customers.map(customer => ({
          value: customer.id,
          label: customer.name
        })), {
          value: NEW_CUSTOMER,
          label: '+ Opret ny kunde'
        }]} data-id="element-298" data-mp="18iu01q86nsu3" />
          {creatingCustomer && <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-299" data-mp="6tdizqz7q0f7">
              <TextField id="ns-navn" label="Navn" required value={name} onChange={event => setName(event.target.value)} data-id="element-300" data-mp="6uydjglj1jd2" />
              <TextField id="ns-tlf" label="Telefonnummer" type="tel" value={phone} onChange={event => setPhone(event.target.value)} data-id="element-301" data-mp="ksmx8l3uykgj" />
              <TextField id="ns-mail" label="E-mail" type="email" className="sm:col-span-2" value={email} onChange={event => setEmail(event.target.value)} data-id="element-302" data-mp="2mj89a73m58q0" />
            </div>}
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-303" data-mp="216ogzjkqeomc">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-304" data-mp="1w8mwqvbkkj9a">Opgave</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-305" data-mp="18isn0mmjrat9">
            <TextField id="ns-titel" label="Opgavetitel" required placeholder="Fx montering af hegn" value={title} onChange={event => setTitle(event.target.value)} className="sm:col-span-2" data-id="element-306" data-mp="18h95ksp5mtpw" />
            <TextField id="ns-adresse" label="Adresse" placeholder="Vej, nummer, postnr. og by" value={address} onChange={event => setAddress(event.target.value)} className="sm:col-span-2" data-id="element-307" data-mp="1dglq36sjzopb" />
            <SelectField id="ns-kategori" label="Kategori" value={category} onChange={event => setCategory(event.target.value as CaseCategory)} options={caseCategories.map(option => ({
            value: option,
            label: option
          }))} data-id="element-308" data-mp="2wf0449kr3w0m" />
            <TextField id="ns-dato" label="Planlagt dato" type="date" value={plannedDate} onChange={event => setPlannedDate(event.target.value)} data-id="element-309" data-mp="2rfx6vbb0iu37" />
          </div>
          <TextArea id="ns-beskrivelse" label="Opgavebeskrivelse" className="mt-5" rows={4} value={description} onChange={event => setDescription(event.target.value)} data-id="element-310" data-mp="367pbotd6kpqo" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-311" data-mp="1zqu5q4ef9zus">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-312" data-mp="1zor6quvakwdx">Pris og status</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-313" data-mp="5cpzhyhl0xx2">
            <SelectField id="ns-prisform" label="Prisform" value={priceModel} onChange={event => setPriceModel(event.target.value as PriceModel)} options={priceModels.map(option => ({
            value: option,
            label: option
          }))} data-id="element-314" data-mp="ck5wcrr51cc" />
            <SelectField id="ns-status" label="Status" value={status} onChange={event => setStatus(event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
            value: option,
            label: option
          }))} data-id="element-315" data-mp="3iqba9e0167lh" />
            {priceModel === 'Timepris' && <TextField id="ns-timepris" label="Timepris (kr.)" inputMode="numeric" value={hourlyRate} onChange={event => setHourlyRate(event.target.value)} data-id="element-316" data-mp="34s3idkhupwh6" />}
            {priceModel === 'Fast pris' && <TextField id="ns-fastpris" label="Fast pris (kr.)" inputMode="numeric" value={fixedPrice} onChange={event => setFixedPrice(event.target.value)} data-id="element-317" data-mp="2putx0112x308" />}
          </div>
          <TextArea id="ns-noter" label="Interne noter" className="mt-5" rows={3} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." value={internalNote} onChange={event => setInternalNote(event.target.value)} data-id="element-318" data-mp="2yp8du819cllg" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-319" data-mp="2fx5zx4g03jew">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-320" data-mp="3md8b7qt2ra90">Billeder</h2>
          <label htmlFor="ns-billeder" className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-321" data-mp="3rc2vyyalr8vr">
            <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-322" data-mp="3brt2nl54ut6m" />
            <span className="text-[15px] font-medium text-ink" data-id="element-323" data-mp="3714ae3lmps6f">Vælg billeder</span>
            <span className="text-sm text-ink-muted" data-id="element-324" data-mp="36z9mcgf29jy4">Du kan tilføje flere billeder</span>
            <input id="ns-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => setPhotos(Array.from(event.target.files ?? []).map(file => ({
            url: URL.createObjectURL(file),
            name: file.name
          })))} data-id="element-325" data-mp="3ls3eb1birsjo" />
          </label>
          {photos.length > 0 && <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-id="element-326" data-mp="1wdf1iyz0o8l4">
              {photos.map(photo => <li key={photo.name} data-id="element-327" data-mp="1q8pj2s5kk5ut">
                  <img src={photo.url} alt={photo.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-328" data-mp="1q7o103q4yj3l" />
                </li>)}
            </ul>}
        </section>

        <div className="flex flex-col gap-3 sm:flex-row" data-id="element-329" data-mp="12imy0w02o05p">
          <Button type="submit" size="lg" data-id="element-330" data-mp="12l938ozhq3kr">
            Gem sag
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/admin/sager')} data-id="element-331" data-mp="17jsqcsptllep">
            Annuller
          </Button>
        </div>
      </form>
    </div>;
}